import React, { useMemo } from "react";
import {
  startOfWeek,
  addDays,
  format,
  isBefore,
  isToday,
  startOfMonth,
  endOfMonth,
  endOfWeek,
} from "date-fns";
import { useGetMovesQuery } from "@/store/moves/apis";
import { useGetCalendarEventsQuery } from "@/store/mover/apis";
import { CalendarEvent, Move } from "@/types";
import { Link } from "react-router-dom";
import { MOVE_COLORS, MOVE_COLORS_LIGHT } from "@/constants";
import { cn } from "@/lib/utils";
import { Loading } from "./Loading";

const MonthCalendar: React.FC<{ currentDate: Date }> = ({ currentDate }) => {
  const firstDayOfMonth = startOfMonth(currentDate);
  const lastDayOfMonth = endOfMonth(currentDate);
  const firstDayOfCalendar = startOfWeek(firstDayOfMonth, { weekStartsOn: 1 });
  const lastDayOfCalendar = endOfWeek(lastDayOfMonth, { weekStartsOn: 1 });

  // Calculate the actual date range we're displaying
  const { displayStartDate, displayEndDate } = useMemo(() => {
    const startDate = firstDayOfCalendar;
    const endDate = lastDayOfCalendar;
    return {
      displayStartDate: startDate,
      displayEndDate: endDate,
    };
  }, [firstDayOfCalendar, lastDayOfCalendar]);

  const {
    data: moves,
    isLoading,
    error,
  } = useGetMovesQuery({
    type: "jobs",
    after: format(displayStartDate, "yyyy-MM-dd"),
    before: format(displayEndDate, "yyyy-MM-dd"),
  });

  const { data: onsiteEvents } = useGetCalendarEventsQuery({
    nature: "onsite",
    after: format(displayStartDate, "yyyy-MM-dd"),
    before: format(displayEndDate, "yyyy-MM-dd"),
  });

  const { data: blockEvents } = useGetCalendarEventsQuery({
    nature: "block",
    after: format(displayStartDate, "yyyy-MM-dd"),
    before: format(displayEndDate, "yyyy-MM-dd"),
  });

  const renderEvent = (event: CalendarEvent | Move) => {
    const isMove = "identifier" in event;
    const move_id = isMove ? event.id : event.move_id;
    let color = "bg-yellow-500";
    if (isMove) {
      color =
        MOVE_COLORS[event.service as keyof typeof MOVE_COLORS] ||
        MOVE_COLORS.default;
    } else if (event.nature === "onsite") {
      color = MOVE_COLORS.onsite;
    }
    const title = isMove ? event.customer_name : event.title;

    return (
      <Link
        key={event.id}
        className={cn("text-black w-full")}
        to={`/move/${move_id}`}
      >
        <div
          key={event.id}
          className={cn(
            "w-full flex items-center gap-2 mb-1 px-2 py-0.5",
            MOVE_COLORS_LIGHT[
              ("service" in event
                ? event.service
                : "onsite") as keyof typeof MOVE_COLORS_LIGHT
            ] || MOVE_COLORS_LIGHT.default
          )}
        >
          <div className={`w-2 h-2 ${color}`} />
          <span className="text-sm truncate">{title}</span>
        </div>
      </Link>
    );
  };

  const renderDay = (date: Date) => {
    const dateString = format(date, "yyyy-MM-dd");
    const isPastOrToday = isBefore(date, new Date()) || isToday(date);
    const isCurrentMonth = date.getMonth() === currentDate.getMonth();
    const bookedMoves =
      moves?.filter(
        (move: Move) => move.service_date && move.service_date === dateString
      ) || [];

    const onsites =
      onsiteEvents?.filter(
        (event: CalendarEvent) => event.date === dateString
      ) || [];

    const blocks =
      blockEvents?.filter(
        (event: CalendarEvent) => event.date === dateString
      ) || [];

    const isBlocked = blocks.length > 0;
    const allEvents = [...bookedMoves, ...onsites, ...blocks];

    return (
      <div
        key={dateString}
        className={`border h-48 overflow-y-auto ${
          !isCurrentMonth ? "bg-gray-50" : isPastOrToday ? "bg-white" : ""
        } ${isBlocked ? "bg-slate-200" : ""}`}
      >
        <h3
          className={`text-sm font-normal p-1 ${
            !isCurrentMonth
              ? "text-black"
              : isToday(date)
              ? "bg-blue-100"
              : isBlocked
              ? "bg-slate-300"
              : "bg-stone-100"
          }`}
        >
          {format(date, "EEE, MMM d")}
        </h3>
        <div className="p-2">
          {isBlocked ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-slate-700 font-medium bg-slate-100 rounded-md px-3 py-1.5 shadow-sm flex items-center gap-2">
                <span>🚫</span>
                <span>Blocked</span>
              </div>
            </div>
          ) : (
            allEvents.map(renderEvent)
          )}
        </div>
      </div>
    );
  };

  const renderWeek = (weekStart: Date) => {
    // Check if any day in this week belongs to the selected month
    const hasDaysInSelectedMonth = [0, 1, 2, 3, 4, 5, 6].some(
      (dayOffset) =>
        addDays(weekStart, dayOffset).getMonth() === currentDate.getMonth()
    );

    if (!hasDaysInSelectedMonth) return null;

    return (
      <div
        key={format(weekStart, "yyyy-MM-dd")}
        className="grid grid-cols-7 gap-1"
      >
        {[0, 1, 2, 3, 4, 5, 6].map((dayOffset) =>
          renderDay(addDays(weekStart, dayOffset))
        )}
      </div>
    );
  };

  if (isLoading)
    return (
      <div className="mt-4">
        <Loading />
      </div>
    );
  if (error) return <div className="mt-4">Error loading data</div>;
  if (!moves) return null;

  return (
    <div>
      {[0, 1, 2, 3, 4, 5].map((weekOffset) => {
        const weekStart = addDays(firstDayOfCalendar, weekOffset * 7);
        return renderWeek(weekStart);
      })}
    </div>
  );
};

export default MonthCalendar;
